import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ErrorCode, type ApiError } from "@racoon/api";

/** Errors no retry will fix: the caller lacks the permission for this list. */
const FORBIDDEN_CODES = new Set<ErrorCode | undefined>([
  ErrorCode.ERROR_CODE_NO_ACCESS,
  ErrorCode.ERROR_CODE_PERMISSION_DENIED,
]);

/**
 * Any RTK Query hook whose argument carries a `list_filter` (currentPage/pageSize)
 * and whose response carries the rows plus a `list_info`.
 */
type ListQueryHook<TData> = (
  arg: any,
  options?: { skip?: boolean },
) => {
  data?: TData;
  isFetching: boolean;
  isError?: boolean;
  error?: unknown;
  /** Changes on every successful fetch of the current argument, refetches included. */
  fulfilledTimeStamp?: number;
  refetch?: () => void;
};

type ListEndpoint<TData> = {
  initiate: (
    arg: any,
    options: { subscribe: false; forceRefetch: true },
  ) => (...thunkArgs: any[]) => { unwrap: () => Promise<TData> };
};

/**
 * One page's request: the caller's arg with this hook's paging set on its list_filter.
 * Anything else the caller put there — a sort — stays, so every page is cut from the same
 * order.
 */
export const withPage = (
  arg: Record<string, unknown>,
  currentPage: number,
  pageSize: number,
): Record<string, unknown> => ({
  ...arg,
  listFilter: { ...(arg.listFilter as object | undefined), currentPage, pageSize },
});

export async function fetchPagesBefore<TData, TRow>(
  fetchPage: (arg: Record<string, unknown>) => Promise<TData>,
  arg: Record<string, unknown>,
  pageSize: number,
  count: number,
  selectRows: (data: TData) => TRow[] | undefined,
): Promise<Record<number, TRow[]>> {
  const fresh = await Promise.all(
    Array.from({ length: count }, (_, index) =>
      fetchPage(withPage(arg, index, pageSize)).then((data): [number, TRow[]] => [
        index,
        [...(selectRows(data) ?? [])],
      ]),
    ),
  );
  return Object.fromEntries(fresh) as Record<number, TRow[]>;
}

type UseAccumulatedListArgs<TData, TRow> = {
  useListQuery: ListQueryHook<TData>;
  listEndpoint: ListEndpoint<TData>;
  /**
   * The request. Pagination is owned by this hook: it sets `listFilter.currentPage` and
   * `pageSize`, keeping the rest of a `listFilter` the caller passes (its sort).
   */
  arg: Record<string, unknown>;
  pageSize: number;
  /**
   * Serialized filter state. Any change resets the accumulator back to page 0, so
   * changing a date range or a filter never mixes rows from two different queries.
   */
  filterKey: string;
  selectRows: (data: TData) => TRow[] | undefined;
  selectTotal: (data: TData) => number | string | undefined;
  /**
   * Identity of a row. Optional, but worth passing: with offset pagination a refetch
   * that inserts a row pushes the page's last row onto the next page, where it would
   * otherwise show up twice.
   */
  selectRowKey?: (row: TRow) => string;
  skip?: boolean;
};

export type AccumulatedList<TRow> = {
  /** Every row loaded so far, in page order. */
  rows: TRow[];
  totalItems: number;
  isFetching: boolean;
  /** The last page request failed. Scrolling to the end retries it once. */
  isError: boolean;
  hasMore: boolean;
  /** Request the next page. A no-op while a page is in flight or the list is exhausted. */
  loadNextPage: () => void;
};

/**
 * Incremental "load more as you scroll" list on top of a paginated RPC.
 *
 * Pages are held as separate slices and flattened for the caller, so a page that is
 * refetched — a mutation invalidating the list's tag, say — replaces its own rows in
 * place instead of being dropped or appended twice. Only the current page stays
 * subscribed, so when it is refetched every page before it is fetched again through
 * `listEndpoint` without a subscription — otherwise an edit to a row from an earlier
 * page would never show up. The guard for "already merged" is
 * the query's `fulfilledTimeStamp`, which is stable across renders and changes on every
 * successful fetch; response identity is not, since a caller may pass a hook with an
 * inline `selectFromResult`.
 *
 * `loadNextPage` fires at most once per accumulated snapshot, so a scroll handler
 * calling it repeatedly cannot skip a page. A failed page releases that guard, making
 * the next call a retry rather than a no-op.
 *
 * Rows are kept when `skip` flips to true, so switching away from a view and back lands
 * on the list as it was rather than on page 0.
 */
export function useAccumulatedList<TData, TRow>({
  useListQuery,
  listEndpoint,
  arg,
  pageSize,
  filterKey,
  selectRows,
  selectTotal,
  selectRowKey,
  skip = false,
}: UseAccumulatedListArgs<TData, TRow>): AccumulatedList<TRow> {
  const [page, setPage] = useState(0);
  const [resetToken, setResetToken] = useState(0);
  const [pages, setPages] = useState<{
    slices: Record<number, TRow[]>;
    totalItems: number;
    /** Set when a settled page came back short: there is nothing after it to load. */
    exhausted: boolean;
  }>({ slices: {}, totalItems: 0, exhausted: false });

  const {
    data: pageData,
    isFetching,
    isError = false,
    error,
    fulfilledTimeStamp,
    refetch,
  } = useListQuery(withPage(arg, page, pageSize), { skip });

  const dispatch = useDispatch();
  const argRef = useRef(arg);
  argRef.current = arg;
  const generationRef = useRef(0);

  const mergedStampRef = useRef<Record<number, number | undefined>>({});
  const prevFilterKeyRef = useRef(filterKey);
  // Guards loadNextPage against advancing more than once per accumulated snapshot.
  const lastRequestedCountRef = useRef(0);

  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      mergedStampRef.current = {};
      generationRef.current += 1;
      lastRequestedCountRef.current = 0;
      setPage(0);
      setPages((prev) =>
        Object.keys(prev.slices).length > 0
          ? { slices: {}, totalItems: 0, exhausted: false }
          : prev,
      );
      setResetToken((token) => token + 1);
      return;
    }
    // Merge only settled, successful responses: while a fetch is in flight, or after
    // it failed, `pageData` still holds the previous arguments' rows.
    if (skip || !pageData || isFetching || isError) return;
    // A hook that does not report the timestamp degrades to merging each page once,
    // the way this hook behaved before — never to merging nothing.
    const stamp = fulfilledTimeStamp ?? 0;
    const previousStamp = mergedStampRef.current[page];
    if (previousStamp === stamp) return;
    mergedStampRef.current[page] = stamp;
    const rows = selectRows(pageData) ?? [];
    setPages((prev) => ({
      slices: { ...prev.slices, [page]: [...rows] },
      totalItems: Number(selectTotal(pageData) ?? 0),
      // A page shorter than requested is the last one, whatever the reported total says
      // — without this a stale or inflated total keeps `hasMore` true forever.
      exhausted: rows.length < pageSize,
    }));
    if (previousStamp === undefined || page === 0) return;
    const generation = generationRef.current;
    fetchPagesBefore(
      (pageArg) =>
        (
          dispatch(
            listEndpoint.initiate(pageArg, { subscribe: false, forceRefetch: true }) as any,
          ) as unknown as { unwrap: () => Promise<TData> }
        ).unwrap(),
      argRef.current,
      pageSize,
      page,
      selectRows,
    )
      .then((fresh) => {
        if (generation !== generationRef.current) return;
        setPages((prev) => ({ ...prev, slices: { ...prev.slices, ...fresh } }));
      })
      .catch(() => {});
  }, [
    pageData,
    fulfilledTimeStamp,
    page,
    pageSize,
    isFetching,
    filterKey,
    skip,
    selectRows,
    selectTotal,
    resetToken,
    dispatch,
    listEndpoint,
  ]);

  // A failed page leaves the row count untouched, so the snapshot guard below would
  // swallow every later call. Retry that page instead — once, since the scroll handler
  // keeps firing while the end of the list is on screen. The guard is released by a
  // success or a new filter only: releasing it when `isError` drops would count the
  // retry's own pending state as recovery, and an error that repeats (a 403) would
  // loop forever.
  const retriedForErrorRef = useRef(false);
  useEffect(() => {
    retriedForErrorRef.current = false;
  }, [fulfilledTimeStamp, filterKey]);

  // A permission error is final: offering the scroll handler "more" would only re-ask.
  const isForbidden = isError && FORBIDDEN_CODES.has((error as ApiError | undefined)?.code);

  const rows = useMemo(() => {
    const ordered = Object.keys(pages.slices)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((index) => pages.slices[index]);
    if (!selectRowKey) return ordered;
    const seen = new Set<string>();
    return ordered.filter((row) => {
      const key = selectRowKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [pages.slices, selectRowKey]);

  // A failed page counts as "more to come" even when nothing merged: page 0 failing
  // leaves totalItems at 0, and without this the table would drop the scroll handler
  // and strand an empty list with no way back.
  const hasMore =
    !isForbidden && (isError || (!pages.exhausted && rows.length < pages.totalItems));

  const loadNextPage = useCallback(() => {
    if (skip || isFetching || !hasMore) return;
    if (isError) {
      if (retriedForErrorRef.current) return;
      retriedForErrorRef.current = true;
      refetch?.();
      return;
    }
    if (lastRequestedCountRef.current === rows.length) return;
    lastRequestedCountRef.current = rows.length;
    setPage((prev) => prev + 1);
  }, [skip, isFetching, hasMore, isError, refetch, rows.length]);

  return {
    rows,
    totalItems: pages.totalItems,
    isFetching: skip ? false : isFetching,
    isError,
    hasMore,
    loadNextPage,
  };
}
